import { copyS3Files } from 'services'
import type { DocumentFile } from 'stories/components'

/** One file carried by an internal `'document'` drag, however the source spelled it. */
export interface DraggedFile {
  fileKey: string
  name: string
}

/**
 * Flatten a `'document'` drag item to the files it carries. The Documents floater emits either a
 * single row (`{ key | fileKey, name }`) or, for a multi-select, `{ documents: [...] }` of rows;
 * other sources, such as exception attachments, use the same shape.
 */
export const dragItemToFiles = (item: Record<string, any>): DraggedFile[] => {
  const rows: Record<string, any>[] = Array.isArray(item?.documents) && item.documents.length ? item.documents : [item]
  return rows
    .map((row) => ({
      fileKey: String(row?.fileKey || row?.key || ''),
      name: String(row?.name || String(row?.fileKey || row?.key || '').split('/').pop() || 'document'),
    }))
    .filter((row) => !!row.fileKey)
}

/**
 * Whether an internal drag brings files the panel does not already hold. A row dragged out of
 * the panel and dropped back on it is a no-op, not a request to duplicate it.
 */
export const isForeignDocumentDrop = (item: Record<string, any>, heldKeys: string[]) => {
  if (!item || item.files) return false
  const files = dragItemToFiles(item)
  return !!files.length && files.some((file) => !heldKeys.includes(file.fileKey))
}

/**
 * Copy the files an internal drag carries into the loan's document folder and describe them the
 * way a fresh upload is described, ready for the categorize-and-save table. Files the panel
 * already holds are skipped.
 */
export const copyDroppedDocuments = async (
  item: Record<string, any>,
  loanNumber: number,
  heldKeys: string[],
): Promise<DocumentFile[]> => {
  const files = dragItemToFiles(item).filter((file) => !heldKeys.includes(file.fileKey))
  if (!files.length) return []
  const keys = await copyS3Files(
    files.map((file) => file.fileKey),
    `loan/${loanNumber}/document`,
  )
  const now = Date.now()
  return files.map((file, index) => ({
    id: now + index,
    name: file.name,
    fileKey: keys[index],
    status: 'Not Reviewed',
    category: '',
    createdAt: now,
  }))
}
